import { useState } from "react";
import "./css/SigningFlow.css";
import { STEPS } from "../constants/steps";
import SignatureModal from "../../../components/signature/SignatureModal"; //"../.. components/signature/SignatureModal";
import { normalizeHtmlEntities } from "../../../api/helpers";

export default function SignatureScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {

    const [risksAccepted, setRisksAccepted] = useState(false);

    const [signature, setSignature] = useState("");
    const [signatureMethod, setSignatureMethod] = useState("");
    const [typedSignature, setTypedSignature] = useState("");
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    const signatureConsentMessage = flowData?.signatureConsentMessage || "";
    const signatureConsentPreMessage = flowData?.signatureConsentPreMessage || "";

    
    const sections = flowData?.waiverTemplate?.sections || [];
    const finalAcknowledgement = sections[sections.length - 1];

    //to show typed signature if not signed.
    const profile = flowContext?.profile || flowData?.profile || {};
    const profileFullName = [
        profile.firstName,
        profile.lastName
    ].filter(Boolean).join(" ").trim();

    const guardian = flowData?.guardian || {};
    const guardianFullName = [
        guardian.firstName,
        guardian.lastName
    ].filter(Boolean).join(" ").trim();

    const signerFullName =
        flowContext?.flowType === "GUARDIAN"
            ? guardianFullName
            : profileFullName;
    
    const goBack = () => {
        if (loading) return;

        setError("");

        next({
            action: "go_back"
        });
    };

    const handleDrawSignature = () => {
        if (loading) return;

        // Switching to Draw means Type is no longer selected
        setTypedSignature("");
        setSignature("");
        setSignatureMethod("DRAW");
        setSignatureModalOpen(true);
    };

    const handleTypeSignature = () => {
        if (loading) return;

        setSignatureMethod("TYPE");
        setTypedSignature(signerFullName);
        setSignature(
            signerFullName
                ? createTypedSignatureImage(signerFullName)
                : ""
        );
        setError("");
    };

    const createTypedSignatureImage = (text) => {
        const canvas = document.createElement("canvas");

        canvas.width = 800;
        canvas.height = 200;

        const ctx = canvas.getContext("2d");

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.font = 'italic 52px "Brush Script MT", "Segoe Script", cursive';
        ctx.fillStyle = "#000";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        return canvas.toDataURL("image/png");
    };

    const submit = () => {
        if (loading) return;

        setError("");

        if (!signature) {
            setError("Please provide your signature before continuing.");
            return;
        }

        if (!signatureMethod) {
            setError("Please choose Draw Signature or Type Signature.");
            return;
        }

        if (!risksAccepted) {
            setError(
                "Please confirm that you understand the waiver and accept the risks before continuing."
            );
            return;
        }

        next({
            action: "submit_waiver",
            signature,
            signatureMethod,
            signatureText:
                signatureMethod === "TYPE"
                    ? typedSignature.trim()
                    : null,
            risksAccepted: true
        });
    };

    if (signatureConsentMessage.trim().length < 20 || signatureConsentPreMessage.trim().length < 20) {
        return (
            <div className="page">
                <div className="page-content">
                    <div className="text-red-600 mt-2">
                        Signature consent message is unavailable or invalid.
                        Please contact support.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`page ${loading ? "page-loading" : ""}`}>
            
            {loading && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2">
                    {error}
                </div>
            )}
            
            <div className="waiver-header">

                <button
                    className="waiver-back"
                    onClick={goBack}
                >
                    ← Back
                </button>

            </div>

            <div className="page-content signature-content">

                {/* REAL FINAL ACKNOWLEDGEMENT FROM WAIVER */}
                {finalAcknowledgement && (
                    <div className="final-acknowledgement">

                        {finalAcknowledgement.title && (
                            <h2 className="signature-title">
                                {normalizeHtmlEntities(finalAcknowledgement.title)}
                            </h2>
                        )}

                        <div
                            className="signature-text"
                            dangerouslySetInnerHTML={{
                                __html: normalizeHtmlEntities(
                                    finalAcknowledgement.content || ""
                                )
                            }}
                        />

                    </div>
                )}

                <h3 className="signature-heading">
                    Signature
                </h3>

                {signatureMethod !== "TYPE" && (
                <>
                    <p className="signature-subtitle">
                        Use your finger to sign below.
                    </p>

                    <div
                        className="signature-pad"
                        onClick={() => {
                            if (!loading) {
                                setSignatureMethod("DRAW");
                                setSignatureModalOpen(true);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        {signature ? (
                            <img
                                src={signature}
                                alt="Signature"
                                className="signature-preview"
                            />
                        ) : (
                            <>
                                <div className="signature-icon">
                                    ✍
                                </div>

                                <div className="signature-placeholder">
                                    Draw signature here
                                </div>
                            </>
                        )}
                    </div>

                    {signature && (
                        <button
                            type="button"
                            className="signature-link"
                            onClick={() => {
                                if (!loading) {
                                    setSignatureMethod("DRAW");
                                    setSignatureModalOpen(true);
                                }
                            }}
                            disabled={loading}
                        >
                            Redraw signature
                        </button>
                    )}

                    {signature && (
                        <button
                            type="button"
                            className="signature-link"
                            onClick={() => {
                                setSignature("");
                                setSignatureMethod("");
                            }}
                            disabled={loading}
                        >
                            Clear signature
                        </button>
                    )}

                    {!signature && (
                        <>
                            <div className="signature-or">
                                Or
                            </div>

                            <button
                                type="button"
                                className="type-signature-link"
                                onClick={handleTypeSignature}
                                disabled={loading}
                            >
                                Can't draw? Type your full legal name
                            </button>
                        </>
                    )}
                </>
            )}

            {signatureMethod === "TYPE" && (
                <div className="typed-signature-section">

                    <input
                        type="text"
                        className="typed-signature-input"
                        value={typedSignature}
                        readOnly
                        disabled={loading}
                    />

                    <div className="edit-profile-name-section">
                        <div className="edit-profile-name-question">
                            Not your correct name?
                        </div>
                        <button
                            type="button"
                            className="signature-link"
                            onClick={() => {
                                if (!loading) {
                                    next({
                                        action: "edit_profile_name"
                                    });
                                }
                            }}
                            disabled={loading}
                        >
                            {flowContext?.flowType === "GUARDIAN"
                                ? "Edit Guardian Name"
                                : "Edit Profile Name"}
                        </button>
                    </div>

                    {typedSignature && (
                        <div className="typed-signature-preview">
                            {typedSignature}
                        </div>
                    )}

                    <button
                        type="button"
                        className="signature-link"
                        onClick={() => {
                            setTypedSignature("");
                            setSignature("");
                            setSignatureMethod("");
                        }}
                        disabled={loading}
                    >
                        Clear signature
                    </button>

                    <div className="signature-or">
                        Or
                    </div>

                    <button
                        type="button"
                        className="type-signature-link"
                        onClick={handleDrawSignature}
                        disabled={loading}
                    >
                        Draw your signature instead
                    </button>

                </div>
            )}


            {/* PRIOR SIGNED WAIVER */}
            {flowData?.lastSignedFullWaiver && (
                <>
                    <div className="prior-waiver-divider"></div>

                    <div className="prior-waiver-link">
                        <button
                            type="button"
                            className="prior-waiver-link"
                            onClick={() => {
                                if (!loading) {
                                    next({
                                        action: "view_last_signed_waiver"
                                    });
                                }
                            }}
                            disabled={loading}
                        >
                            View prior signed waiver on{" "}
                            {new Date(
                                flowData.lastSignedFullWaiver.completedAt
                            ).toLocaleDateString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "2-digit",
                            })}
                        </button>
                    </div>
                </>
            )}
              
            
                <p className="signature-disclaimer">
                    {signatureConsentPreMessage}
                </p>

                <div className="form-group">

                    <label>
                        Date
                    </label>

                    <input
                        type="text"
                        value={new Date().toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                        })}
                        readOnly
                    />

                </div>

                <div className="guardian-checkbox-card">

                    <input
                        type="checkbox"
                        id="acceptWaiver"
                        checked={risksAccepted}
                        onChange={(e) => {
                            setRisksAccepted(e.target.checked);
                            setError("");
                        }}
                        disabled={loading}
                    />

                    <label htmlFor="acceptWaiver">
                        {signatureConsentMessage}
                    </label>

                </div>

                <div className="signature-required">

                    🔒 Signature and confirmation required.

                </div>

            </div>

            <div className="page-footer">

                <button
                    className={`primary-button ${
                        loading || !risksAccepted || !signature || !signatureMethod
                            ? "disabled-button"
                            : ""
                    }`}
                    disabled={loading || !risksAccepted || !signature || !signatureMethod}
                    onClick={submit}
                >
                    {loading ? "Generating Waiver..." : "Submit Waiver"}
                </button>

            </div>

            <SignatureModal
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                onSave={(savedSignature) => {
                    setSignature(savedSignature);
                    setSignatureMethod("DRAW");
                    setTypedSignature("");
                    setSignatureModalOpen(false);
                    setError("");
                }}
            />

        </div>
    );
}